/**
 * The hardware module's paths.
 *
 * `/hardware` is the tracker listing and the module's root. An SAV desk arrives with
 * "which of these needs somebody today", not with an IMEI, so nesting the listing under
 * a device would make the morning screen unreachable without first choosing the very
 * unit nobody has identified yet — the same argument `features/tyres/routes.ts` makes.
 *
 * Route paths stay in English while the UI defaults to French: the address bar is not a
 * translated surface, and a localized set would mean two routing tables and a redirect
 * on every language switch.
 */

export const HARDWARE_PATH = '/hardware'

export const HARDWARE_QUERY_KEYS = {
  /** `unexplained` | `sim_expired` | `power_expected` | `never_reported` | `reporting`. */
  silence: 'silence',
  /** `expired` | `expiring` | `ok` | `unknown`. */
  simState: 'sim',
  vehicleId: 'vehicle',
  search: 'q',
  /** Which tracker's dossier is open, so a finding forwards as a link. */
  selected: 'device',
} as const

/** A deep link into one reporting state — what a tile links to. */
export function hardwarePath(silence?: string): string {
  return silence === undefined
    ? HARDWARE_PATH
    : `${HARDWARE_PATH}?${HARDWARE_QUERY_KEYS.silence}=${silence}`
}

/** One vehicle's trackers, as a filtered listing rather than a route of its own. */
export function vehicleHardwarePath(vehicleId: string): string {
  return `${HARDWARE_PATH}?${HARDWARE_QUERY_KEYS.vehicleId}=${vehicleId}`
}

export function deviceDossierPath(deviceId: string): string {
  return `${HARDWARE_PATH}?${HARDWARE_QUERY_KEYS.selected}=${deviceId}`
}

/** The SIM cards about to lapse — what the renewal tile links to. */
export function expiringSimsPath(): string {
  return `${HARDWARE_PATH}?${HARDWARE_QUERY_KEYS.simState}=expiring`
}
